import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Github,
  FileCode,
  Check,
  AlertCircle,
  Loader2,
  ChevronDown,
  Search,
} from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import {
  getGithubRepositories,
  getGithubWorkflows,
  getGithubWorkflowInputs,
} from "../services/storageService";

interface AutomationConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  onSave: (config: {
    githubRepo: string;
    githubWorkflow: string;
    githubBranch: string;
    githubInputs?: Record<string, any>;
  }) => void;
  onRevert?: () => void;
  initialRepo?: string;
  initialWorkflow?: string;
  initialBranch?: string;
  initialInputs?: Record<string, any>;
}

const AutomationConfigModal: React.FC<AutomationConfigModalProps> = ({
  isOpen,
  onClose,
  orgId,
  onSave,
  onRevert,
  initialRepo = "",
  initialWorkflow = "",
  initialBranch = "main",
  initialInputs = {},
}) => {
  const { getToken } = useAuth();
  const [repo, setRepo] = useState(initialRepo);
  const [workflow, setWorkflow] = useState(initialWorkflow);
  const [branch, setBranch] = useState(initialBranch);
  const [inputs, setInputs] = useState<Record<string, any>>(initialInputs);
  const [inputDefinitions, setInputDefinitions] = useState<Record<string, any>>(
    {},
  );
  const [error, setError] = useState("");
  const [isLoadingRepos, setIsLoadingRepos] = useState(false);
  const [isLoadingWorkflows, setIsLoadingWorkflows] = useState(false);
  const [isLoadingInputs, setIsLoadingInputs] = useState(false);
  const [repositories, setRepositories] = useState<
    { id: number; name: string; full_name: string }[]
  >([]);
  const [workflows, setWorkflows] = useState<
    { id: number; name: string; path: string; state: string }[]
  >([]);

  const [repoSearch, setRepoSearch] = useState("");
  const [workflowSearch, setWorkflowSearch] = useState("");
  const [isRepoDropdownOpen, setIsRepoDropdownOpen] = useState(false);
  const [isWorkflowDropdownOpen, setIsWorkflowDropdownOpen] = useState(false);

  const repoRef = useRef<HTMLDivElement>(null);
  const workflowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (repoRef.current && !repoRef.current.contains(event.target as Node)) {
        setIsRepoDropdownOpen(false);
      }
      if (
        workflowRef.current &&
        !workflowRef.current.contains(event.target as Node)
      ) {
        setIsWorkflowDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setRepo(initialRepo);
      setWorkflow(initialWorkflow);
      setBranch(initialBranch || "main");
      setInputs(initialInputs || {});
      setError("");
      fetchRepositories();

      // Create synthetic definitions from stored inputs so they display immediately
      if (initialInputs && Object.keys(initialInputs).length > 0) {
        const syntheticDefs: Record<string, any> = {};
        Object.entries(initialInputs).forEach(([key, value]) => {
          syntheticDefs[key] = {
            description: key,
            required: false,
            type:
              typeof value === "boolean"
                ? "boolean"
                : typeof value === "number"
                  ? "number"
                  : "string",
            default: value,
          };
        });
        setInputDefinitions(syntheticDefs);
      }
    }
  }, [isOpen, initialRepo, initialWorkflow, initialInputs]);

  useEffect(() => {
    if (repo && repo.includes("/")) {
      fetchWorkflows(repo);
    } else {
      setWorkflows([]);
    }
  }, [repo]);

  useEffect(() => {
    if (repo && repo.includes("/") && workflow) {
      fetchInputs(repo, workflow);
    } else {
      setInputDefinitions({});
    }
  }, [repo, workflow]);

  const fetchRepositories = async () => {
    setIsLoadingRepos(true);
    try {
      const token = await getToken();
      const data = await getGithubRepositories(orgId, token || undefined);
      setRepositories(data);
    } catch (e: any) {
      setError("Failed to load GitHub repositories");
    } finally {
      setIsLoadingRepos(false);
    }
  };

  const fetchWorkflows = async (repoFullName: string) => {
    setIsLoadingWorkflows(true);
    try {
      const token = await getToken();
      const [owner, name] = repoFullName.split("/");
      const data = await getGithubWorkflows(
        orgId,
        owner,
        name,
        token || undefined,
      );
      setWorkflows(data);
    } catch (e: any) {
      // Don't show global error if repo is invalid or no workflows
      setWorkflows([]);
    } finally {
      setIsLoadingWorkflows(false);
    }
  };

  const fetchInputs = async (repoFullName: string, workflowPath: string) => {
    setIsLoadingInputs(true);
    try {
      const token = await getToken();
      const [owner, name] = repoFullName.split("/");
      const data = await getGithubWorkflowInputs(
        orgId,
        owner,
        name,
        workflowPath,
        token || undefined,
      );
      setInputDefinitions(data);

      // Initialize inputs with default values if not already set
      setInputs((prev) => {
        const newInputs = { ...prev };
        Object.entries(data).forEach(([key, definition]: [string, any]) => {
          if (
            newInputs[key] === undefined &&
            definition.default !== undefined
          ) {
            newInputs[key] = definition.default;
          }
        });
        return newInputs;
      });
    } catch (e: any) {
      // If we already have synthetic definitions, don't clear them on fetch error
      setInputDefinitions((prev) => {
        if (Object.keys(prev).length === 0) return {};
        return prev;
      });
    } finally {
      setIsLoadingInputs(false);
    }
  };

  if (!isOpen) return null;

  const handleSave = () => {
    if (!repo.trim()) {
      setError("Repository is required");
      return;
    }
    if (!workflow.trim()) {
      setError("Workflow file path is required");
      return;
    }
    onSave({
      githubRepo: repo,
      githubWorkflow: workflow,
      githubBranch: branch || "main",
      githubInputs: inputs,
    });
    onClose();
  };

  const filteredRepos = repositories.filter((r) =>
    r.full_name.toLowerCase().includes(repoSearch.toLowerCase()),
  );

  const filteredWorkflows = workflows.filter(
    (w) =>
      w.name.toLowerCase().includes(workflowSearch.toLowerCase()) ||
      w.path.toLowerCase().includes(workflowSearch.toLowerCase()),
  );

  const renderInput = (key: string, def: any) => {
    const value = inputs[key] ?? "";

    if (def.type === "choice") {
      return (
        <select
          value={value}
          onChange={(e) => setInputs({ ...inputs, [key]: e.target.value })}
          className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
        >
          {!def.required && <option value="">None</option>}
          {def.options?.map((opt: string) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }

    if (def.type === "boolean") {
      return (
        <label className="flex items-center gap-3 cursor-pointer group">
          <div className="relative inline-flex items-center">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) =>
                setInputs({ ...inputs, [key]: e.target.checked })
              }
              className="sr-only"
            />
            <div
              className={`w-10 h-5 rounded-full transition-colors ${value ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-700"}`}
            />
            <div
              className={`absolute left-1 w-3 h-3 bg-white rounded-full transition-transform ${value ? "translate-x-5" : "translate-x-0"}`}
            />
          </div>
          <span className="text-sm text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-slate-200 transition-colors">
            {value ? "Enabled" : "Disabled"}
          </span>
        </label>
      );
    }

    return (
      <input
        type={def.type === "number" ? "number" : "text"}
        value={value}
        onChange={(e) => setInputs({ ...inputs, [key]: e.target.value })}
        placeholder={def.default?.toString() || ""}
        className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
      />
    );
  };

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div
        className="w-full max-w-md bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50 rounded-t-xl">
          <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
              <Github size={20} />
            </div>
            <h3 className="font-bold text-slate-900 dark:text-slate-100">
              Automation Settings
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-500 dark:hover:text-slate-300 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-lg flex items-center gap-2">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2" ref={repoRef}>
              <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                GitHub Repository
              </label>
              <div className="relative">
                <div
                  className={`flex items-center gap-3 w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-slate-100 cursor-pointer hover:border-indigo-500 transition-all ${isRepoDropdownOpen ? "ring-2 ring-indigo-500 border-indigo-500" : ""}`}
                  onClick={() => setIsRepoDropdownOpen(!isRepoDropdownOpen)}
                >
                  <Github size={16} className="text-slate-400" />
                  <span className="flex-1 truncate">
                    {repo || (
                      <span className="text-slate-400">Select repository</span>
                    )}
                  </span>
                  {isLoadingRepos ? (
                    <Loader2
                      size={16}
                      className="animate-spin text-slate-400"
                    />
                  ) : (
                    <ChevronDown size={16} className="text-slate-400" />
                  )}
                </div>

                {isRepoDropdownOpen && (
                  <div className="absolute z-dropdown w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="p-2 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2">
                      <Search size={14} className="text-slate-400" />
                      <input
                        autoFocus
                        type="text"
                        value={repoSearch}
                        onChange={(e) => setRepoSearch(e.target.value)}
                        placeholder="Search repositories..."
                        className="w-full bg-transparent border-none outline-none text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredRepos.length > 0 ? (
                        filteredRepos.map((r) => (
                          <div
                            key={r.id}
                            className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors flex items-center justify-between ${repo === r.full_name ? "text-indigo-600 dark:text-indigo-400 bg-indigo-50/50 dark:bg-indigo-900/20 font-medium" : "text-slate-700 dark:text-slate-300"}`}
                            onClick={() => {
                              setRepo(r.full_name);
                              setWorkflow("");
                              setIsRepoDropdownOpen(false);
                            }}
                          >
                            <span className="truncate">{r.full_name}</span>
                            {repo === r.full_name && <Check size={14} />}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-slate-400 text-center italic">
                          No repositories found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <p className="text-[10px] text-slate-400">
                The repository where your workflow is defined.
              </p>
            </div>

            <div className="space-y-2" ref={workflowRef}>
              <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                Workflow File
              </label>
              <div className="relative">
                <div
                  className={`flex items-center gap-3 w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-slate-100 cursor-pointer hover:border-indigo-500 transition-all ${isWorkflowDropdownOpen ? "ring-2 ring-indigo-500 border-indigo-500" : ""} ${!repo ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() =>
                    repo && setIsWorkflowDropdownOpen(!isWorkflowDropdownOpen)
                  }
                >
                  <FileCode size={16} className="text-slate-400" />
                  <span className="flex-1 truncate">
                    {workflow || (
                      <span className="text-slate-400">
                        {repo ? "Select workflow" : "Select repository first"}
                      </span>
                    )}
                  </span>
                  {isLoadingWorkflows ? (
                    <Loader2
                      size={16}
                      className="animate-spin text-slate-400"
                    />
                  ) : (
                    <ChevronDown size={16} className="text-slate-400" />
                  )}
                </div>

                {isWorkflowDropdownOpen && repo && (
                  <div className="absolute z-dropdown w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="p-2 border-b border-slate-100 dark:border-slate-700 flex items-center gap-2">
                      <Search size={14} className="text-slate-400" />
                      <input
                        autoFocus
                        type="text"
                        value={workflowSearch}
                        onChange={(e) => setWorkflowSearch(e.target.value)}
                        placeholder="Search workflows..."
                        className="w-full bg-transparent border-none outline-none text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredWorkflows.length > 0 ? (
                        filteredWorkflows.map((w) => (
                          <div
                            key={w.id}
                            className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors flex flex-col ${workflow === w.path ? "text-indigo-600 dark:text-indigo-400 bg-indigo-50/50 dark:bg-indigo-900/20 font-medium" : "text-slate-700 dark:text-slate-300"}`}
                            onClick={() => {
                              setWorkflow(w.path);
                              setIsWorkflowDropdownOpen(false);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <span className="truncate">{w.name}</span>
                              {workflow === w.path && <Check size={14} />}
                            </div>
                            <span className="text-[10px] opacity-60 truncate">
                              {w.path}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-sm text-slate-400 text-center italic">
                          No active workflows found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <p className="text-[10px] text-slate-400">
                Path to the YAML workflow file in the repository.
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                Branch / Ref
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  placeholder="main"
                  className="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all"
                />
              </div>
              <p className="text-[10px] text-slate-400">
                The branch or git ref to trigger the workflow on.
              </p>
            </div>

            {/* Inputs */}
            {(isLoadingInputs ||
              Object.entries(inputDefinitions).length > 0) && (
              <div className="space-y-4 pt-4 border-t border-slate-100 dark:border-slate-800 animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                    Workflow Inputs
                  </label>
                  {isLoadingInputs && (
                    <Loader2
                      size={12}
                      className="animate-spin text-slate-400"
                    />
                  )}
                </div>

                {isLoadingInputs &&
                Object.keys(inputDefinitions).length === 0 ? (
                  <div className="space-y-3">
                    <div className="h-10 bg-slate-50 dark:bg-slate-800/50 rounded-lg animate-pulse" />
                    <div className="h-10 bg-slate-50 dark:bg-slate-800/50 rounded-lg animate-pulse opacity-50" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(inputDefinitions).map(
                      ([key, def]: [string, any]) => (
                        <div key={key} className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              {def.description || key}
                              {def.required && (
                                <span className="text-red-500 ml-1">*</span>
                              )}
                            </label>
                          </div>
                          {renderInput(key, def)}
                        </div>
                      ),
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50/50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center gap-3 rounded-b-xl">
          <div>
            {onRevert && (
              <button
                onClick={() => {
                  onRevert();
                  onClose();
                }}
                className="text-xs font-bold text-red-500 hover:text-red-600 dark:hover:text-red-400 transition-colors uppercase tracking-wide"
              >
                Revert to Manual
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              data-testid="save-automation-config"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 rounded-lg shadow-sm shadow-indigo-500/20 flex items-center gap-2 transition-all hover:scale-105"
            >
              <Check size={16} />
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationConfigModal;
